// src/components/tasks/RoutineEditorModal.tsx
// Modal for creating and editing visual schedule templates (routines)
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { IonIcon, IonReorder, IonReorderGroup, IonItem, IonRange, IonLabel } from '@ionic/react';
import { trash, reorderThree, close, pencil } from 'ionicons/icons';
import { Modal, ModalSection } from '../ui/Modal';
import { TextInput } from '../ui/TextInput';
import { FormSelect, type SelectOption } from '../ui/FormSelect';
import { Button } from '../ui/Button';
import { IconPicker } from '../ui/IconPicker';
import { Stack, Row } from '../ui/Layout';
import type { ScheduleTemplate, ScheduleTemplateItem, ScheduleCategory, Task, FamilyMember } from '../../types';
import './tasks.css';

export interface RoutineEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  template?: ScheduleTemplate | null;
  tasks: Task[];
  familyMembers: FamilyMember[];
  onCreate: (data: Partial<ScheduleTemplate> & { name: string; schedule_category: string }) => Promise<ScheduleTemplate | null>;
  onUpdate: (templateId: string, updates: Partial<ScheduleTemplate>) => Promise<ScheduleTemplate | null>;
  onDelete: (templateId: string) => Promise<boolean>;
  onAddItem: (templateId: string, taskId: string, options?: { scheduled_time?: string; display_order?: number }) => Promise<ScheduleTemplateItem | null>;
  onUpdateItem: (itemId: string, updates: Partial<ScheduleTemplateItem>) => Promise<ScheduleTemplateItem | null>;
  onRemoveItem: (itemId: string) => Promise<boolean>;
  onReorderItems: (updates: Array<{ id: string; display_order: number }>) => Promise<boolean>;
  onCreateTask?: (task: { name: string; icon: string; points_value: number }) => Promise<Task | null>;
}

const CATEGORY_OPTIONS: SelectOption<ScheduleCategory>[] = [
  { id: 'morning', label: 'Morning', value: 'morning', icon: <span>🌅</span> },
  { id: 'after_school', label: 'After School', value: 'after_school', icon: <span>🏫</span> },
  { id: 'evening', label: 'Evening', value: 'evening', icon: <span>🌆</span> },
  { id: 'bedtime', label: 'Bedtime', value: 'bedtime', icon: <span>🌙</span> },
  { id: 'weekend', label: 'Weekend', value: 'weekend', icon: <span>🎮</span> },
  { id: 'anytime', label: 'Any Time', value: 'anytime', icon: <span>⏰</span> },
];

const DAY_CHIPS = [
  { value: 0, label: 'S', full: 'Sunday' },
  { value: 1, label: 'M', full: 'Monday' },
  { value: 2, label: 'T', full: 'Tuesday' },
  { value: 3, label: 'W', full: 'Wednesday' },
  { value: 4, label: 'T', full: 'Thursday' },
  { value: 5, label: 'F', full: 'Friday' },
  { value: 6, label: 'S', full: 'Saturday' },
];

export const RoutineEditorModal: React.FC<RoutineEditorModalProps> = ({
  isOpen,
  onClose,
  template,
  tasks,
  familyMembers,
  onCreate,
  onUpdate,
  onDelete,
  onAddItem,
  onRemoveItem,
  onReorderItems,
  onCreateTask,
}) => {
  const isEditing = !!template;

  // Form state
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('📋');
  const [category, setCategory] = useState<ScheduleCategory>('morning');
  const [assignedTo, setAssignedTo] = useState<string[]>([]);
  const [autoAssign, setAutoAssign] = useState(false);
  const [autoAssignDays, setAutoAssignDays] = useState<number[]>([]);
  const [isActive, setIsActive] = useState(true);

  // Items state
  const [items, setItems] = useState<ScheduleTemplateItem[]>([]);

  // UI state
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [taskSearchValue, setTaskSearchValue] = useState('');

  // Task edit state
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [showTaskEditor, setShowTaskEditor] = useState(false);
  const [editTaskForm, setEditTaskForm] = useState({ name: '', icon: '📋', points_value: 1 });
  const [showTaskIconPicker, setShowTaskIconPicker] = useState(false);

  // Reset form when template changes
  useEffect(() => {
    if (template) {
      setName(template.name);
      setIcon(template.icon || '📋');
      setCategory(template.schedule_category);
      // Support both legacy single assigned_to and new array format
      const assignees = template.assigned_to_ids
        || (template.assigned_to ? [template.assigned_to] : []);
      setAssignedTo(assignees);
      setAutoAssign(template.auto_assign);
      setAutoAssignDays(template.auto_assign_days || []);
      setIsActive(template.is_active);
      setItems(template.items || []);
    } else {
      setName('');
      setIcon('📋');
      setCategory('morning');
      setAssignedTo([]);
      setAutoAssign(false);
      setAutoAssignDays([]);
      setIsActive(true);
      setItems([]);
    }
    setTaskSearchValue('');
  }, [template, isOpen]);

  // Task options for adding
  const taskOptions: SelectOption<string>[] = useMemo(() => {
    const itemTaskIds = new Set(items.map(i => i.task_id));
    return tasks
      .filter(t => t.is_active && !itemTaskIds.has(t.id))
      .map(t => ({
        id: t.id,
        label: t.name,
        value: t.id,
        icon: <span style={{ fontSize: 18 }}>{t.icon || '📋'}</span>,
      }));
  }, [tasks, items]);

  // Member options for chips
  const memberChips = useMemo(() =>
    familyMembers.map(m => ({
      id: m.id,
      label: m.nickname || m.name,
      avatar: m.avatar_url,
      emoji: m.avatar_emoji || '👤',
    })),
  [familyMembers]);

  // Toggle member selection
  const toggleMember = (memberId: string) => {
    setAssignedTo(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  // Toggle day selection
  const toggleDay = (day: number) => {
    setAutoAssignDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort()
    );
  };

  // Add task to routine
  const handleAddTask = async (taskId: string) => {
    if (!template) {
      // If creating new, add to local state
      const task = tasks.find(t => t.id === taskId);
      if (task) {
        setItems(prev => [
          ...prev,
          {
            id: `temp-${Date.now()}`,
            template_id: '',
            task_id: taskId,
            display_order: prev.length,
            is_optional: false,
            created_at: new Date().toISOString(),
            task,
          },
        ]);
      }
    } else {
      // If editing, save immediately
      await onAddItem(template.id, taskId, { display_order: items.length });
    }
    setTaskSearchValue('');
  };

  // Remove task from routine
  const handleRemoveTask = async (itemId: string) => {
    if (itemId.startsWith('temp-')) {
      setItems(prev => prev.filter(i => i.id !== itemId));
    } else {
      await onRemoveItem(itemId);
      setItems(prev => prev.filter(i => i.id !== itemId));
    }
  };

  // Quick create a new task from the FormSelect creatable and add it to the routine
  const handleCreateTask = async (taskName: string) => {
    if (!taskName.trim() || !onCreateTask) return;

    const newTask = await onCreateTask({
      name: taskName.trim(),
      icon: '📋',
      points_value: 1,
    });

    if (newTask) {
      // Add to items
      setItems(prev => [
        ...prev,
        {
          id: `temp-${Date.now()}`,
          template_id: template?.id || '',
          task_id: newTask.id,
          display_order: prev.length,
          is_optional: false,
          created_at: new Date().toISOString(),
          task: newTask,
        },
      ]);

      // If editing existing template, also add to database
      if (template) {
        await onAddItem(template.id, newTask.id, { display_order: items.length });
      }

      // Clear search
      setTaskSearchValue('');
    }
  };

  // Handle reorder
  const handleReorder = useCallback((event: CustomEvent) => {
    event.detail.complete();
    const from = event.detail.from;
    const to = event.detail.to;

    setItems(prev => {
      const newItems = [...prev];
      const [moved] = newItems.splice(from, 1);
      newItems.splice(to, 0, moved);
      return newItems.map((item, index) => ({ ...item, display_order: index }));
    });
  }, []);

  // Save reorder on blur or save
  const saveReorder = async () => {
    if (!template) return;
    const updates = items
      .filter(i => !i.id.startsWith('temp-'))
      .map((i, index) => ({ id: i.id, display_order: index }));
    if (updates.length > 0) {
      await onReorderItems(updates);
    }
  };

  // Save routine
  const handleSave = async () => {
    if (!name.trim()) return;

    setSaving(true);
    try {
      if (isEditing && template) {
        await saveReorder();
        await onUpdate(template.id, {
          name: name.trim(),
          icon,
          schedule_category: category,
          // Legacy: first member or undefined for backwards compatibility
          assigned_to: assignedTo[0] || undefined,
          // New: array of all selected members
          assigned_to_ids: assignedTo.length > 0 ? assignedTo : undefined,
          auto_assign: autoAssign,
          auto_assign_days: autoAssignDays,
          is_active: isActive,
        });
      } else {
        const newTemplate = await onCreate({
          name: name.trim(),
          icon,
          schedule_category: category,
          // Legacy: first member or undefined for backwards compatibility
          assigned_to: assignedTo[0] || undefined,
          // New: array of all selected members
          assigned_to_ids: assignedTo.length > 0 ? assignedTo : undefined,
          auto_assign: autoAssign,
          auto_assign_days: autoAssignDays,
          is_active: isActive,
        });

        // Add items to new template
        if (newTemplate) {
          for (const item of items) {
            await onAddItem(newTemplate.id, item.task_id, { display_order: item.display_order });
          }
        }
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  // Delete routine
  const handleDelete = async () => {
    if (!template) return;
    if (confirm(`Delete "${template.name}"? This cannot be undone.`)) {
      await onDelete(template.id);
      onClose();
    }
  };

  // Open task editor
  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    setEditTaskForm({
      name: task.name,
      icon: task.icon || '📋',
      points_value: task.points_value || 1,
    });
    setShowTaskEditor(true);
  };

  // Save edited task
  const handleSaveTaskEdit = async () => {
    if (!editingTask || !editTaskForm.name.trim()) return;

    // Update the task in the database via Supabase
    const { supabase } = await import('../../services/supabase');
    const { error } = await supabase
      .from('tasks')
      .update({
        name: editTaskForm.name.trim(),
        icon: editTaskForm.icon,
        points_value: editTaskForm.points_value,
      })
      .eq('id', editingTask.id);

    if (!error) {
      // Update local items state to reflect the change
      setItems(prev => prev.map(item => {
        if (item.task_id === editingTask.id && item.task) {
          return {
            ...item,
            task: {
              ...item.task,
              name: editTaskForm.name.trim(),
              icon: editTaskForm.icon,
              points_value: editTaskForm.points_value,
            },
          };
        }
        return item;
      }));
    }

    setShowTaskEditor(false);
    setEditingTask(null);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        header={{ title: isEditing ? 'Edit Routine' : 'Create Routine' }}
        footer={{
          primaryAction: {
            label: saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Routine'),
            onClick: handleSave,
            disabled: !name.trim() || saving,
          },
          secondaryAction: {
            label: 'Cancel',
            onClick: onClose,
          },
        }}
      >
        <ModalSection spacing="normal">
          <Stack gap="lg">
            {/* Name and Icon Row */}
            <Row gap="md" align="start">
              <div>
                <div className="routine-editor__label">Icon</div>
                <button
                  type="button"
                  className="routine-editor__icon-btn"
                  onClick={() => setShowIconPicker(true)}
                >
                  {icon}
                </button>
              </div>
              <div style={{ flex: 1 }}>
                <TextInput
                  label="Name"
                  value={name}
                  onChange={setName}
                  placeholder="e.g., Morning Routine"
                  variant="filled"
                />
              </div>
            </Row>

            {/* Category */}
            <FormSelect<ScheduleCategory>
              label="Time of Day"
              value=""
              onChange={() => {}}
              options={CATEGORY_OPTIONS}
              selectedOption={CATEGORY_OPTIONS.find(c => c.value === category) || null}
              onSelect={opt => setCategory(opt.value)}
              placeholder="Select category..."
              searchable={false}
            />

            {/* Assign to (multi-select) */}
            <div>
              <div className="routine-editor__label">Assign To</div>
              <Row gap="xs" wrap>
                {memberChips.map(member => (
                  <button
                    key={member.id}
                    type="button"
                    className={`routine-editor__member-chip ${assignedTo.includes(member.id) ? 'active' : ''}`}
                    onClick={() => toggleMember(member.id)}
                  >
                    {member.avatar ? (
                      <img src={member.avatar} alt="" className="routine-editor__member-avatar" />
                    ) : (
                      <span className="routine-editor__member-emoji">{member.emoji}</span>
                    )}
                    <span>{member.label}</span>
                  </button>
                ))}
              </Row>
              <div className="routine-editor__hint">
                {assignedTo.length === 0
                  ? 'Tap to select family members (or leave empty for anyone)'
                  : `Assigned to ${assignedTo.length} member${assignedTo.length > 1 ? 's' : ''}`}
              </div>
            </div>

            {/* Auto-assign Days */}
            <div>
              <div className="routine-editor__label">Auto-assign on</div>
              <Row gap="xs" wrap>
                {DAY_CHIPS.map(day => (
                  <button
                    key={day.value}
                    type="button"
                    className={`routine-editor__day-chip ${autoAssignDays.includes(day.value) ? 'active' : ''}`}
                    onClick={() => {
                      toggleDay(day.value);
                      setAutoAssign(true);
                    }}
                    title={day.full}
                  >
                    {day.label}
                  </button>
                ))}
              </Row>
              <div className="routine-editor__hint">
                {autoAssignDays.length === 0
                  ? 'Select days to auto-generate tasks'
                  : `Tasks will be created on ${autoAssignDays.map(d => DAY_CHIPS[d].full).join(', ')}`}
              </div>
            </div>

            {/* Tasks List */}
            <div>
              <div className="routine-editor__label">Tasks in this Routine</div>

              {items.length === 0 ? (
                <div className="routine-editor__empty-tasks">
                  No tasks added yet. Add tasks below.
                </div>
              ) : (
                <IonReorderGroup disabled={false} onIonItemReorder={handleReorder}>
                  {items.map((item, index) => {
                    const task = item.task || tasks.find(t => t.id === item.task_id);
                    return (
                      <IonItem key={item.id} lines="none" className="routine-editor__task-item">
                        <IonReorder slot="start">
                          <IonIcon icon={reorderThree} className="routine-editor__reorder-icon" />
                        </IonReorder>
                        <span className="routine-editor__task-order">{index + 1}</span>
                        <span className="routine-editor__task-icon">{task?.icon || '📋'}</span>
                        <span className="routine-editor__task-name">{task?.name || 'Unknown Task'}</span>
                        <div className="routine-editor__task-actions" slot="end">
                          {task && (
                            <button
                              type="button"
                              className="routine-editor__edit-btn"
                              onClick={() => handleEditTask(task)}
                              title="Edit task"
                            >
                              <IonIcon icon={pencil} />
                            </button>
                          )}
                          <button
                            type="button"
                            className="routine-editor__remove-btn"
                            onClick={() => handleRemoveTask(item.id)}
                            title="Remove from routine"
                          >
                            <IonIcon icon={close} />
                          </button>
                        </div>
                      </IonItem>
                    );
                  })}
                </IonReorderGroup>
              )}

              {/* Add Task */}
              <div className="routine-editor__add-task">
                <FormSelect<string>
                  label=""
                  value={taskSearchValue}
                  onChange={setTaskSearchValue}
                  options={taskOptions}
                  selectedOption={null}
                  onSelect={opt => handleAddTask(opt.value)}
                  placeholder="Search or create task..."
                  searchable
                  clearable={false}
                  creatable={!!onCreateTask}
                  onCreate={handleCreateTask}
                  createLabel="Create task"
                  noResultsMessage="No matching tasks"
                />
              </div>
            </div>

            {/* Delete button for existing */}
            {isEditing && (
              <Button
                variant="danger"
                fill="clear"
                size="small"
                onClick={handleDelete}
                style={{ alignSelf: 'flex-start' }}
              >
                <IonIcon icon={trash} slot="start" />
                Delete Routine
              </Button>
            )}
          </Stack>
        </ModalSection>
      </Modal>

      {/* Icon Picker Modal */}
      <Modal
        isOpen={showIconPicker}
        onClose={() => setShowIconPicker(false)}
        header={{ title: 'Choose Icon' }}
        size="md"
      >
        <ModalSection spacing="normal">
          <IconPicker
            value={{ type: 'emoji', name: icon }}
            onChange={(val) => {
              setIcon(val?.type === 'emoji' ? val.name : '📋');
              setShowIconPicker(false);
            }}
            tabs={['emoji']}
            iconSize={32}
            maxHeight={400}
          />
        </ModalSection>
      </Modal>

      {/* Task Editor Modal */}
      <Modal
        isOpen={showTaskEditor}
        onClose={() => {
          setShowTaskEditor(false);
          setEditingTask(null);
        }}
        header={{ title: 'Edit Task' }}
        size="sm"
        footer={{
          primaryAction: {
            label: 'Save',
            onClick: handleSaveTaskEdit,
            disabled: !editTaskForm.name.trim(),
          },
          secondaryAction: {
            label: 'Cancel',
            onClick: () => {
              setShowTaskEditor(false);
              setEditingTask(null);
            },
          },
        }}
      >
        <ModalSection spacing="normal">
          <Stack gap="md">
            {/* Icon and Name Row */}
            <Row gap="md" align="start">
              <div>
                <div className="routine-editor__label">Icon</div>
                <button
                  type="button"
                  className="routine-editor__icon-btn"
                  onClick={() => setShowTaskIconPicker(true)}
                >
                  {editTaskForm.icon}
                </button>
              </div>
              <div style={{ flex: 1 }}>
                <TextInput
                  label="Task Name"
                  value={editTaskForm.name}
                  onChange={(v) => setEditTaskForm(f => ({ ...f, name: v }))}
                  placeholder="Task name"
                  variant="filled"
                />
              </div>
            </Row>

            {/* Points */}
            <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0' }}>
              <IonLabel position="stacked">Points: {editTaskForm.points_value}</IonLabel>
              <IonRange
                min={1}
                max={10}
                value={editTaskForm.points_value}
                onIonChange={e => setEditTaskForm(f => ({ ...f, points_value: e.detail.value as number }))}
                pin
                pinFormatter={(value: number) => `${value}`}
              />
            </IonItem>
          </Stack>
        </ModalSection>
      </Modal>

      {/* Task Icon Picker Modal */}
      <Modal
        isOpen={showTaskIconPicker}
        onClose={() => setShowTaskIconPicker(false)}
        header={{ title: 'Choose Task Icon' }}
        size="md"
      >
        <ModalSection spacing="normal">
          <IconPicker
            value={{ type: 'emoji', name: editTaskForm.icon }}
            onChange={(val) => {
              setEditTaskForm(f => ({ ...f, icon: val?.type === 'emoji' ? val.name : '📋' }));
              setShowTaskIconPicker(false);
            }}
            tabs={['emoji']}
            iconSize={32}
            maxHeight={400}
          />
        </ModalSection>
      </Modal>

    </>
  );
};

RoutineEditorModal.displayName = 'RoutineEditorModal';

export default RoutineEditorModal;
